import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KeywordCriteria } from "@/types/peopleSearch";
import { ChipsInput } from "./ChipsInput";

// Chip color conventions follow AdvancedSearchPanel.tsx:
// emerald = required, blue = any-of, red = excluded
const REQUIRED_CHIP = 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300';
const ANY_OF_CHIP = 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300';
const EXCLUDED_CHIP = 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-400 dark:text-red-500 line-through opacity-60';

interface KeywordClauseEditorProps {
	value: KeywordCriteria;
	onChange: (value: KeywordCriteria) => void;
}

export function KeywordClauseEditor({ value, onChange }: KeywordClauseEditorProps) {
	const { t } = useTranslation();

	const updateGroup = (index: number, terms: string[]) => {
		const anyOfGroups = value.anyOfGroups.map((g, i) => i === index ? terms : g);
		onChange({ ...value, anyOfGroups });
	};

	const removeGroup = (index: number) => {
		onChange({ ...value, anyOfGroups: value.anyOfGroups.filter((_, i) => i !== index) });
	};

	return (
		<div className="space-y-6">
			<div>
				<h4 className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1">
					{t("peopleSearch.keywords.required", "Required keywords")}
				</h4>
				<p className="text-xs text-muted-foreground mb-2">
					{t("peopleSearch.keywords.requiredHint", "Every keyword must match")}
				</p>
				<ChipsInput
					values={value.required}
					onChange={required => onChange({ ...value, required })}
					placeholder={t("peopleSearch.keywords.requiredPlaceholder", "Enter required skills and keywords")}
					chipClassName={REQUIRED_CHIP}
				/>
			</div>

			<div>
				<h4 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
					{t("peopleSearch.keywords.anyOf", "Any-of keyword groups")}
				</h4>
				<p className="text-xs text-muted-foreground mb-2">
					{t("peopleSearch.keywords.anyOfHint", "Within a group, one match is enough. Every group must have a match.")}
				</p>
				<div className="space-y-3">
					{value.anyOfGroups.map((group, index) => (
						<div key={index} className="border rounded-sm p-3">
							<div className="flex items-center justify-between mb-2">
								<span className="text-xs text-muted-foreground">
									{t("peopleSearch.keywords.group", "Group {{number}}", { number: index + 1 })}
								</span>
								<Button
									variant="ghost"
									size="sm"
									className="h-6 w-6 p-0"
									onClick={() => removeGroup(index)}
									aria-label={t("peopleSearch.keywords.removeGroup", "Remove group")}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</Button>
							</div>
							<ChipsInput
								values={group}
								onChange={terms => updateGroup(index, terms)}
								placeholder={t("peopleSearch.keywords.anyOfPlaceholder", "Add alternatives (e.g. Python, Java)")}
								chipClassName={ANY_OF_CHIP}
							/>
						</div>
					))}
					<Button
						variant="outline"
						size="sm"
						onClick={() => onChange({ ...value, anyOfGroups: [...value.anyOfGroups, []] })}
					>
						<Plus className="h-4 w-4 mr-1" />
						{t("peopleSearch.keywords.addGroup", "Add group")}
					</Button>
				</div>
			</div>

			<div>
				<h4 className="text-sm font-medium text-red-500 mb-1">
					{t("peopleSearch.keywords.excluded", "Exclude keywords")}
				</h4>
				<p className="text-xs text-muted-foreground mb-2">
					{t("peopleSearch.keywords.excludedHint", "Exclude profiles with these keywords")}
				</p>
				<ChipsInput
					values={value.excluded}
					onChange={excluded => onChange({ ...value, excluded })}
					placeholder={t("peopleSearch.keywords.excludedPlaceholder", "Exclude profiles with these keywords")}
					chipClassName={EXCLUDED_CHIP}
				/>
			</div>
		</div>
	);
}
